import { Injectable } from '@angular/core';
import { EnhancedDatasetResult } from '@mode/shared/contract-common';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { SwitchViewReorderingFacade } from '@mode/switch-views/contract';
import { SwitchViewReorderingService } from './switch-view-reordering.service';

type ReorderingAction = 'MOVE_UP' | 'MOVE_DOWN' | 'MOVE_TOP' | 'MOVE_TO_BOTTOM' | 'DRAG';

@Injectable()
export class SwitchViewReorderingTrackingService implements SwitchViewReorderingFacade {
  dragOverStream$ = this.switchViewReorderingService.dragOverStream$;
  dropIndex$ = this.switchViewReorderingService.dropIndex$;
  currentDatasetDragIndex$ = this.switchViewReorderingService.currentDatasetDragIndex$;
  sortedEnhancedDatasetResults$ = this.switchViewReorderingService.sortedEnhancedDatasetResults$;

  constructor(
    private switchViewReorderingService: SwitchViewReorderingService,
    private analyticsService: AnalyticsService,
    private metaTags: MetaTagsService
  ) {}

  private track<T>(fn: () => T, reorderingAction: ReorderingAction, reportToken: string): T {
    this.analyticsService.track(AnalyticsEvent.Name.ReorderDataset, {
      action: 'reorder_datasets',
      organizationToken: this.metaTags.getTag(MetaTags.OwnerUsername),
      reportToken,
      reorderingAction,
    });

    const result = fn();

    this.analyticsService.track(AnalyticsEvent.Name.ReorderDataset, {
      action: 'reorder_datasets_successfull',
      organizationToken: this.metaTags.getTag(MetaTags.OwnerUsername),
      reportToken,
      reorderingAction,
    });

    return result;
  }

  onMoveDatasetUp(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.track(
      () => this.switchViewReorderingService.onMoveDatasetUp(reportToken, enhancedDatasetResults, pos),
      'MOVE_UP',
      reportToken
    );
  }

  onMoveDatasetDown(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.track(
      () => this.switchViewReorderingService.onMoveDatasetDown(reportToken, enhancedDatasetResults, pos),
      'MOVE_DOWN',
      reportToken
    );
  }

  onMoveDatasetToTop(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.track(
      () => this.switchViewReorderingService.onMoveDatasetToTop(reportToken, enhancedDatasetResults, pos),
      'MOVE_TOP',
      reportToken
    );
  }

  onMoveDatasetToBottom(reportToken: string, enhancedDatasetResults: EnhancedDatasetResult[], pos: number) {
    return this.track(
      () => this.switchViewReorderingService.onMoveDatasetToBottom(reportToken, enhancedDatasetResults, pos),
      'MOVE_TO_BOTTOM',
      reportToken
    );
  }

  onDatasetDragStart($event: DragEvent, pos: number) {
    return this.switchViewReorderingService.onDatasetDragStart($event, pos);
  }

  onDatasetDragEnd($event: DragEvent, pos: number) {
    return this.switchViewReorderingService.onDatasetDragEnd($event, pos);
  }

  onDatasetDragEnter($event: DragEvent) {
    return this.switchViewReorderingService.onDatasetDragEnter($event);
  }

  onDatasetDragOver($event: DragEvent) {
    return this.switchViewReorderingService.onDatasetDragOver($event);
  }

  onDragOverNonDraggableDropZone($event: DragEvent, enhancedDatasetResults: EnhancedDatasetResult[]) {
    return this.switchViewReorderingService.onDragOverNonDraggableDropZone($event, enhancedDatasetResults);
  }

  onDatasetDragLeave($event: DragEvent) {
    return this.switchViewReorderingService.onDatasetDragLeave($event);
  }

  onDatasetDrop(reportToken: string, $event: DragEvent, enhancedDatasetResults: EnhancedDatasetResult[]) {
    return this.track(
      () => this.switchViewReorderingService.onDatasetDrop(reportToken, $event, enhancedDatasetResults),
      'DRAG',
      reportToken
    );
  }

  calculateDropIndexFromDragEvent($event: DragEvent, dropZone: HTMLElement, indexAttribute: string) {
    return this.switchViewReorderingService.calculateDropIndexFromDragEvent($event, dropZone, indexAttribute);
  }

  pushToDragOverStream($event: DragEvent) {
    return this.switchViewReorderingService.pushToDragOverStream($event);
  }
}
